import { ReactNode } from 'react';
import * as React from 'react';
import { Field } from 'formik';
import styled, { css } from 'styled-components';

import FieldErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/FieldErrorMessage';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import { Box, Flex } from 'reflexbox';

export enum CheckboxTypes {
	Default,
	Decorated,
	RoundedRect,
}

interface IProps {
	id: string;
	name: string;
	label: string;
	value: string;
	icon?: ReactNode;
	tintColor?: string;
	checkboxStyle?: CheckboxTypes;
}

interface ILabel {
	checkboxStyle: CheckboxTypes;
}

const Element = styled(Field)`
	display: none;
`;

const DecoratedCheckboxStyle = css`
	color: ${(props) => props.theme.colors.slateBlue};
	min-height: 40px;
	line-height: 20px;
	display: inline-flex;
	align-items: center;
	width: 100%;
	opacity: 1;
	padding: 8px 48px 8px 72px;
	background-color: ${(props) => props.theme.colors.whiteBlue};
	clip-path: polygon(calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%, 0 0);
	position: relative;

	&:before {
		width: 28px;
		height: 28px;
		top: calc(50% - 14px);
		left: 24px;
		border-radius: 6px;
		border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
		background-color: ${(props) => props.theme.colors.white};
		position: absolute;
	}

	input[type='checkbox']:checked + & {
		background-color: ${(props) => props.theme.colors.primary};
		color: ${(props) => props.theme.colors.white};

		&:before {
			border-color: ${(props) => props.theme.colors.primary};
			background-color: ${(props) => props.theme.colors.primary};
			background-repeat: no-repeat;
			background-size: contain;
			background-image: url('data:image/svg+xml;utf8, ${(props) =>
				escape(
					`<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20"><style type="text/css">* { background-color: ${props.theme.colors.primary}; fill:  ${props.theme.colors.white} }</style><path d="M7 14.17L2.83 10l-1.41 1.41L7 17 19 5l-1.41-1.42L7 14.17z"/></svg>`,
				)}');
		}
	}
`;

const RoundedRectCheckboxStyle = css`
	color: ${(props) => props.theme.colors.slateBlue};
	min-height: 40px;
	line-height: 20px;
	display: flex;
	align-items: center;
	width: 100%;
	opacity: 1;
	padding: 8px 12px 8px 48px;
	background-color: ${(props) => props.theme.colors.whiteBlue};
	position: relative;
	border-radius: 6px;

	&:before {
		width: 28px;
		height: 28px;
		top: calc(50% - 14px);
		left: 8px;
		border-radius: 6px;
		border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
		background-color: ${(props) => props.theme.colors.white};
		position: absolute;
	}

	input[type='checkbox']:checked + & {
		background-color: ${(props) => props.theme.colors.primary};
		color: ${(props) => props.theme.colors.white};

		&:before {
			border-color: ${(props) => props.theme.colors.primary};
			background-color: ${(props) => props.theme.colors.primary};
			background-repeat: no-repeat;
			background-size: contain;
			background-image: url('data:image/svg+xml;utf8, ${(props) =>
				escape(
					`<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20"><style type="text/css">* { background-color: ${props.theme.colors.primary}; fill:  ${props.theme.colors.white} }</style><path d="M7 14.17L2.83 10l-1.41 1.41L7 17 19 5l-1.41-1.42L7 14.17z"/></svg>`,
				)}');
		}
	}
`;

const LabelElement = styled.label<ILabel>`
	color: ${(props) => props.theme.colors.primary};
	opacity: 0.25;
	margin-right: 30px;
	cursor: pointer;
	user-select: none;

	&:before {
		width: 20px;
		height: 20px;
		border-radius: 2px;
		content: '';
		margin-right: 8px;
		display: inline-block;
		vertical-align: middle;
		border: 1px solid ${(props) => props.theme.colors.primary};
	}

	input[type='checkbox']:checked + & {
		opacity: 1;

		svg g g {
			fill: ${(props) => props.theme.colors.yellow};
		}

		&:before {
			background-image: url('data:image/svg+xml;utf8, ${(props) =>
				escape(
					`<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20"><style type="text/css">* { background-color: ${props.theme.colors.primary}; fill:  ${props.theme.colors.white} }</style><path d="M7 14.17L2.83 10l-1.41 1.41L7 17 19 5l-1.41-1.42L7 14.17z"/></svg>`,
				)}');
		}
	}

	svg {
		vertical-align: middle;
		margin-right: 10px;
	}

	${(props) => props.checkboxStyle === CheckboxTypes.Decorated && DecoratedCheckboxStyle}
	${(props) => props.checkboxStyle === CheckboxTypes.RoundedRect && RoundedRectCheckboxStyle}
`;

const TintIndicator = styled.div<{ tintColor: string }>`
	width: 10px;
	height: 10px;
	border-radius: 5px;
	border: 1px solid ${(props) => props.theme.colors.white};
	background-color: ${(props) => props.tintColor};
`;

// TODO: Evaluate if this Checkbox should be used anywhere, it isn't right now.
// eslint-disable-next-line
export default function Checkbox({
	id,
	name,
	label,
	icon,
	value,
	tintColor,
	checkboxStyle = CheckboxTypes.Default,
}: IProps) {
	return (
		<Column width={1}>
			<Element id={id} name={name} type="checkbox" value={value} />
			<LabelElement htmlFor={id} checkboxStyle={checkboxStyle}>
				<Flex justifyContent="space-between" flexGrow={1} alignItems="center">
					<Box>
						{icon}
						{label}
					</Box>
					<Box>{tintColor && <TintIndicator tintColor={tintColor} />}</Box>
				</Flex>
			</LabelElement>
			<FieldErrorMessage name={name} />
		</Column>
	);
}
